import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid';
import client from '../../api/client';
import Spinner from '../Global/Spinner';
import { useAlert } from '../../context/AlertContext';

const money = (n) =>
	n === null || n === undefined
		? '—'
		: new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: 0,
		  }).format(Number(n));

const STATUS_STYLES = {
	draft: 'bg-gray-500/15 text-gray-300 ring-gray-500/30',
	sent: 'bg-sky-500/15 text-sky-300 ring-sky-500/30',
	accepted: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
	declined: 'bg-rose-500/15 text-rose-300 ring-rose-500/30',
};

export default function Proposals() {
	const { setAlert } = useAlert();
	const [proposals, setProposals] = useState([]);
	const [loading, setLoading] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await client.get('/proposal');
			setProposals(data.proposals || []);
		} catch (err) {
			setAlert('error', err?.response?.data?.error || 'Failed to load proposals');
		} finally {
			setLoading(false);
		}
	}, [setAlert]);

	useEffect(() => {
		load();
	}, [load]);

	const remove = async (id) => {
		if (!window.confirm('Delete this proposal?')) return;
		try {
			await client.delete(`/proposal/${id}`);
			setProposals((p) => p.filter((x) => x._id !== id));
			setAlert('success', 'Proposal deleted');
		} catch (err) {
			setAlert('error', err?.response?.data?.error || 'Delete failed');
		}
	};

	return (
		<div className='px-4 pb-24 sm:px-6 lg:px-8'>
			<div className='flex flex-wrap items-end justify-between gap-4'>
				<div>
					<h1 className='text-2xl font-semibold text-white'>Proposals</h1>
					<p className='mt-1 text-sm text-gray-400'>
						Build a solar quote: configure the system, set pricing, and model the
						savings.
					</p>
				</div>
				<Link
					to='/admin/proposals/new'
					className='inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600'
				>
					<PlusIcon className='h-4 w-4' /> New proposal
				</Link>
			</div>

			<div className='mt-6 overflow-hidden rounded-lg ring-1 ring-white/10'>
				{loading ? (
					<div className='py-16'>
						<Spinner color='white' />
					</div>
				) : proposals.length === 0 ? (
					<p className='py-16 text-center text-sm text-gray-400'>
						No proposals yet. Create your first one.
					</p>
				) : (
					<table className='min-w-full divide-y divide-white/10'>
						<thead className='bg-white/5'>
							<tr>
								{['Name', 'Customer', 'Status', 'Net cost', 'Monthly', 'Payback', 'Updated', ''].map(
									(h) => (
										<th
											key={h}
											className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400'
										>
											{h}
										</th>
									)
								)}
							</tr>
						</thead>
						<tbody className='divide-y divide-white/5'>
							{proposals.map((p) => (
								<tr key={p._id} className='hover:bg-white/5'>
									<td className='px-4 py-3 text-sm font-medium text-white'>
										<Link to={`/admin/proposals/${p._id}`} className='hover:text-primary-400'>
											{p.name}
										</Link>
									</td>
									<td className='px-4 py-3 text-sm text-gray-300'>
										{p.customer?.name || '—'}
									</td>
									<td className='px-4 py-3'>
										<span
											className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ring-inset ${
												STATUS_STYLES[p.status] || STATUS_STYLES.draft
											}`}
										>
											{p.status}
										</span>
									</td>
									<td className='px-4 py-3 text-sm text-gray-300'>
										{money(p.summarySnapshot?.netSystemCost)}
									</td>
									<td className='px-4 py-3 text-sm text-gray-300'>
										{p.summarySnapshot?.monthlyPayment
											? `${money(p.summarySnapshot.monthlyPayment)}/mo`
											: '—'}
									</td>
									<td className='px-4 py-3 text-sm text-gray-300'>
										{p.summarySnapshot?.paybackYear
											? `Yr ${p.summarySnapshot.paybackYear}`
											: '—'}
									</td>
									<td className='px-4 py-3 text-sm text-gray-400'>
										{new Date(p.updatedAt).toLocaleDateString()}
									</td>
									<td className='px-4 py-3 text-right'>
										<button
											onClick={() => remove(p._id)}
											className='rounded p-1.5 text-gray-500 hover:bg-rose-500/10 hover:text-rose-400'
											title='Delete'
										>
											<TrashIcon className='h-4 w-4' />
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
}
